import React, { useEffect, useState, useRef, useMemo } from 'react';

import useTranslate from '../../../../common/hooks/useTranslate';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { setPin, removePin, registerDownload } from '../../../services/documentsService';

import { debounce } from 'lodash';
import printJS from 'print-js';
import { saveAs } from '@progress/kendo-file-saver';
import { getZoomFromLocalStorage } from '../../../../common/utility/getZoomFromLocalStorage';

import { RootState } from '../../../../redux/store';
import { PIN, POPUP } from '../../../../redux/types';

import { IBasePdfViewer } from '../../types/IBasePdfViewer';

import { Document, Page } from 'react-pdf';
import PdfDocumentControls from '../pdfDocumentControls/pdfDocumentControls';
import ComponentLoader from '../../../ui/loaders/componentLoader/componentLoader';

import './pdfViewer.scss';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const PdfViewer: React.FC<IBasePdfViewer> = ({
                                               title,
                                               file,
                                               document,
                                               shouldPin,
                                               loading,
                                               controlsBcgColor,
                                               className,
                                               onClose,
                                             }) => {
  const [numPages, setNumPages] = useState<number>(null);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [focusedPage, setFocusedPage] = useState<number>(1);
  const [pages, setPages] = useState<any[]>([]);
  const [zoom, setZoom] = useState<number>(getZoomFromLocalStorage());
  const [pinned, setPinned] = useState<boolean>(false);
  const [expand, setExpand] = useState<boolean>(window.innerWidth < 769);
  const [size, setSize] = useState<any>({
    x: window.innerWidth,
    y: window.innerHeight,
  });

  const { t } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const impersonate = useSelector((state: RootState) => state.impersonate.impersonate);

  const data = useMemo(() => {
    if (file) {
      return { data: file.uint8 };
    } else {
      return null;
    }
  }, [file]);

  const containerRef = useRef(null);

  const topMargin = 6;
  const margin = 16;

  const documentLoaded = (pdfDoc: any) => {
    setNumPages(pdfDoc.numPages);
    setPdfDocument(pdfDoc);
  };

  const loadPages = (pagesToLoad: any, zoomFactor: any) => {
    let startY = 0;
    let endY = 0;

    let loaded = pagesToLoad.map((page: any, index: any) => {
      let width = size.x * 0.8 * zoomFactor;
      let factor = width / page.view[2];
      let height = Math.trunc(page.view[3] * factor);

      startY = endY;
      endY += height + margin; //topMargin

      return {
        pageIndex: index,
        page: page,
        show: index >= focusedPage - 2 && index <= focusedPage,
        width: width,
        height: height,
        startY: startY,
        endY: endY++,
      };
    });
    setPages(loaded);
    return loaded;
  };

  const focusPage = (pageIndex: any) => {
    setFocusedPage(pageIndex + 1);
    setPages(
      pages.map((page, index) => {
        page.show = index >= pageIndex - 1 && index <= pageIndex + 1;
        return page;
      }),
    );
  };

  const handleScroll = debounce((event) => {
    let container = event.target;
    let pageIndex = -1;

    pages.find((page, index) => {
      if (page.startY <= container.scrollTop && page.endY >= container.scrollTop) {
        pageIndex = index;
        return true;
      }
      return false;
    });

    if (pageIndex > -1) {
      focusPage(pageIndex);
    }
  }, 50);

  const onScroll = (event: any) => {
    handleScroll(event);
  };

  const zoomIn = () => {
    changeZoom(zoom + 0.2);
  };

  const zoomOut = () => {
    changeZoom(zoom - 0.2);
  };

  const changeZoom = (zoomFactor: any) => {
    if (zoomFactor <= 0.1) return;
    setZoom(zoomFactor);
    window.localStorage.setItem('zoom', zoomFactor);
    let pagesLoaded = loadPages(
      pages.map((page) => page.page),
      zoomFactor,
    );
    scrollToPage(pagesLoaded[focusedPage - 1], 'auto');
  };

  const nextPage = () => {
    let pageNum = focusedPage + 1;
    if (pageNum > numPages) return;
    setFocusedPage(pageNum);
    scrollToPage(pages[pageNum - 1]);
  };

  const previousPage = () => {
    let pageNum = focusedPage - 1;
    if (pageNum < 1) return;
    setFocusedPage(pageNum);
    scrollToPage(pages[pageNum - 1]);
  };

  const scrollToPage = (page: any, scrollBehavior?: any) => {
    containerRef.current.scrollTo({ top: page.startY, behavior: scrollBehavior || 'smooth' });
  };

  const handlePrint = () => {
    printJS(URL.createObjectURL(file.blob));
  };

  const handleDownload = () => {
    saveAs(file.blob, document ? document.name : 'agreements');

    if (document) {
      registerDownload(document).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  };

  const clickBackHandler = () => {
    onClose ? onClose() : navigate(-1);
  };

  const pinHandler = () => {
    if (!impersonate) {
      if (document) {
        if (pinned === false) {
          setPin(document.id).then(() => {
            setPinned(true);
            dispatch({ type: PIN.PIN_TRIGGER });
          }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
        } else if (pinned === true) {
          removePin(document.id).then(() => {
            setPinned(false);
            dispatch({ type: PIN.PIN_TRIGGER });
          }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
        }
      }
    }
  };

  const updateSize = () => {
    setSize({
      x: window.innerWidth,
      y: window.innerHeight,
    });
  };

  const resize = debounce(updateSize, 100);

  const onExpand = () => {
    setExpand(true);
  };

  const onCollapse = () => {
    setExpand(false);
  };

  useEffect(() => {
    if (!pdfDocument) return;

    let promises = [];
    for (let i = 0; i < numPages; i++) {
      promises.push(pdfDocument.getPage(i + 1));
    }

    Promise.all(promises).then((resolved) => {
      loadPages(resolved, zoom);
    });
  }, [pdfDocument]);

  useEffect(() => {
    pages.length > 0 && changeZoom(zoom);
  }, [size]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    if (shouldPin) {
      setPinned(document.isPinned);
    }
  }, [document]);

  return (
    <div className={`pdf-viewer-container ${expand ? 'expanded-pdf-container' : ''} ${className ? className : ''}`}>
      {title &&
        <div className='pdf-title'>
          <span className='pdf-title-text'>{document?.label}</span>
        </div>
      }
      <PdfDocumentControls
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        zoom={zoom}
        numPages={numPages}
        onNextPage={nextPage}
        onPreviousPage={previousPage}
        onPrint={handlePrint}
        page={focusedPage}
        onDownload={handleDownload}
        onPin={pinHandler}
        pinned={pinned}
        shouldPin={shouldPin}
        onBackClick={clickBackHandler}
        expandClick={onExpand}
        collapseClick={onCollapse}
        expand={expand}
        backgroundColor={controlsBcgColor}
      />

      <div className={`pdf-document-container `} onScroll={onScroll} ref={containerRef}>
        {loading ? (
          <div className='loader-wrapper'>
            <ComponentLoader />
          </div>
        ) : (data.data ? (
            <Document className='pdf-document' file={data} loading={''} onLoadSuccess={documentLoaded}>
              {pages.map((page, index) => {
                return (
                  <div key={page.pageIndex} className='pdf-page'
                       style={{ height: page.height, width: page.width, margin: `${topMargin}px auto ${margin}px auto` }}>
                    {page.show && <Page pageNumber={index + 1} width={page.width} height={page.height} />}
                  </div>
                );
              })}
            </Document>) : (
            <div className="pdf-no-document-container">
              <h1>{t('PDF_VIEWER.NO_DOCUMENT')}</h1>
            </div>
          )
        )}
      </div>

      {/*{data.data ? (*/}
      {/*  <div className={`pdf-document-container `} onScroll={onScroll} ref={containerRef}>*/}
      {/*    {loading ? (*/}
      {/*      <div className='loader-wrapper'>*/}
      {/*        <ComponentLoader />*/}
      {/*      </div>*/}
      {/*    ) : (*/}
      {/*      <Document className='pdf-document' file={data} loading={''} onLoadSuccess={documentLoaded}>*/}
      {/*        {pages.map((page, index) => {*/}
      {/*          return (*/}
      {/*            <div key={page.pageIndex} className='pdf-page'*/}
      {/*                 style={{ height: page.height, width: page.width, margin: `0 auto` }}>*/}
      {/*              {page.show && <Page pageNumber={index + 1} width={page.width} height={page.height} />}*/}
      {/*            </div>*/}
      {/*          );*/}
      {/*        })}*/}
      {/*      </Document>*/}
      {/*    )}*/}
      {/*  </div>*/}
      {/*) : (*/}
      {/*  <div className='pdf-no-document-container'>*/}
      {/*    <h1>{t('PDF_VIEWER.NO_DOCUMENT')}</h1>*/}
      {/*  </div>*/}
      {/*)}*/}

    </div>
  );
};

export default PdfViewer;